// ============================================
// MoltiVerse v3 - HYPER-SCALABLE DATA ENGINE
// ============================================
// Progressive streaming, IndexedDB caching, time-slicing
// Designed for 1M+ agents without memory explosion

import { get, set, del, keys, clear } from 'idb-keyval';
import type { 
  Agent, 
  Connection, 
  GraphData, 
  UniverseStats, 
  TimeSlice,
  Post
} from './types';

// ============ CONFIGURATION ============
const CONFIG = {
  API_BASE: '/api',
  PAGE_SIZE: 500,           // Agents per API request
  STREAM_DELAY: 50,         // ms between chunk processing
  MAX_RETRIES: 5,
  INITIAL_BACKOFF: 1000,    // ms
  MAX_BACKOFF: 30000,       // ms
  CACHE_PREFIX: 'mv3_',
  CACHE_VERSION: 25,        // Bumped to load all agents
  
  // SCALABILITY LIMITS - default to 5K, experimental mode for all
  MAX_RENDERED_NODES: 5000,   // Default: top 5K agents
  MAX_RENDERED_NODES_ALL: 50000, // Experimental: show all agents
  MAX_RENDERED_LINKS: 2000,   // Only used in pathfinder mode
  CLUSTER_THRESHOLD: 100000,  // Cluster only for very large datasets
  USE_SCALABLE_API: true,     // Use smart sampling endpoints
};

// ============ TYPES ============
type StreamCallback = (chunk: { agents?: Agent[]; connections?: Connection[]; progress: number }) => void;
type ErrorCallback = (error: Error) => void;

// ============ UNIVERSE LOADER CLASS ============
export class UniverseLoader {
  private agents: Map<string, Agent> = new Map();
  private connections: Map<string, Connection> = new Map();
  private posts: Map<string, Post> = new Map();
  private timeIndex: Map<number, Set<string>> = new Map(); // timestamp -> agent IDs
  private connectionIndex: Map<string, Set<string>> = new Map(); // agentId -> connected agent IDs
  
  private isLoading = false;
  private abortController: AbortController | null = null;
  private stats: UniverseStats = {
    totalAgents: 0,
    totalPosts: 0,
    totalInteractions: 0,
    loadedAgents: 0,
    loadedConnections: 0,
    cacheSize: 0,
    isStreaming: false,
    streamProgress: 0,
    oldestTimestamp: Date.now(),
    newestTimestamp: Date.now(),
  };

  // ============ INITIALIZATION ============
  
  async initialize(): Promise<void> {
    console.log('🌌 Initializing Universe Loader...');
    
    // Try to load from cache first
    const cached = await this.loadFromCache();
    if (cached) {
      console.log(`📦 Loaded ${this.agents.size} agents from cache`);
    }
    
    // Get current stats from server
    await this.fetchStats();
  }

  // ============ SCALABLE LOADING ============
  
  async streamUniverse(
    onProgress: StreamCallback,
    onError?: ErrorCallback,
    options?: { showAllAgents?: boolean }
  ): Promise<void> {
    if (this.isLoading) {
      console.warn('⚠️ Already streaming');
      return;
    }
    
    // Quick check: if we already have data matching the request, skip
    const targetNodes = options?.showAllAgents ? CONFIG.MAX_RENDERED_NODES_ALL : CONFIG.MAX_RENDERED_NODES;
    if (this.agents.size > 0 && this.agents.size >= Math.min(targetNodes, this.stats.totalAgents || Infinity)) {
      console.log(`✅ Already have ${this.agents.size} agents, skipping reload`);
      onProgress({ progress: 100 });
      return;
    }

    this.isLoading = true;
    this.stats.isStreaming = true;
    this.abortController = new AbortController();
    
    // Immediate progress feedback
    onProgress({ progress: 5 });

    try {
      // Use scalable API for large datasets
      if (CONFIG.USE_SCALABLE_API) {
        await this.loadScalableGraph(onProgress, this.abortController.signal, options?.showAllAgents);
      } else {
        // Legacy: Stream all data (only for small datasets)
        await this.streamAgents(onProgress, this.abortController.signal);
        await this.streamConnections(onProgress, this.abortController.signal);
      }
      
      // Save to cache
      await this.saveToCache();
      
      // Build indexes (time arrays built lazily on first Genesis use)
      this.buildTimeIndex();
      this.buildConnectionIndex();
      this.invalidateTimeArrays();
      
      // Refresh stats from server after loading
      await this.fetchStats();
      
      this.stats.streamProgress = 100;
      onProgress({ progress: 100 });
      
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        console.log('🛑 Streaming aborted');
      } else {
        console.error('❌ Streaming failed:', error);
        onError?.(error as Error);
      }
    } finally {
      this.isLoading = false;
      this.stats.isStreaming = false;
      this.abortController = null;
    }
  }

  /**
   * Load graph using scalable API - handles millions of agents efficiently
   */
  private async loadScalableGraph(
    onProgress: StreamCallback,
    signal: AbortSignal,
    showAllAgents?: boolean
  ): Promise<void> {
    const maxNodes = showAllAgents ? CONFIG.MAX_RENDERED_NODES_ALL : CONFIG.MAX_RENDERED_NODES;
    console.log(`🚀 Loading scalable graph data (max ${maxNodes.toLocaleString()} agents)...`);
    onProgress({ progress: 10 });

    interface ScalableGraphResponse {
      nodes: Array<Agent & { connection_count?: number }>;
      links: Array<{
        source: string;
        target: string;
        weight: number;
        count: number;
        sentiment: number;
        last_interaction?: string;
      }>;
      meta?: {
        totalAgents: number;
        sampledAgents: number;
        sampledLinks: number;
        isSampled: boolean;
      };
    }

    const result = await this.fetchWithRetry<ScalableGraphResponse>(
      `/graph-scalable?maxNodes=${maxNodes}&maxLinks=${CONFIG.MAX_RENDERED_LINKS}`,
      signal
    );

    if (!result) {
      throw new Error('Failed to load scalable graph');
    }

    onProgress({ progress: 50 });

    // BULLETPROOF: Validate response structure
    const nodes = Array.isArray(result.nodes) ? result.nodes : [];
    const links = Array.isArray(result.links) ? result.links : [];
    
    if (nodes.length === 0) {
      console.warn('⚠️ API returned empty nodes array');
    }

    // Process nodes - with null-safe parsing
    const newAgents: Agent[] = [];
    for (const node of nodes) {
      // Skip invalid nodes
      if (!node || !node.id) {
        console.warn('⚠️ Skipping invalid node:', node);
        continue;
      }
      
      // Parse numeric fields safely (DB/JSON may return strings or nulls)
      node.karma = parseInt(String(node.karma ?? 0)) || 0;
      node.post_count = parseInt(String(node.post_count ?? 0)) || 0;
      node.comment_count = parseInt(String(node.comment_count ?? 0)) || 0;
      node._birthTime = node.first_seen ? new Date(node.first_seen).getTime() : Date.now();
      
      // Ensure required fields have defaults
      node.name = node.name || 'unknown';
      node.main_submolt = node.main_submolt || 'general';
      node.is_bot = Boolean(node.is_bot);
      
      this.agents.set(node.id, node);
      newAgents.push(node);
    }

    onProgress({ agents: newAgents, progress: 70 });

    // Process links - with null-safe parsing
    const newConnections: Connection[] = [];
    for (const link of links) {
      // Skip invalid links
      if (!link || !link.source || !link.target) {
        console.warn('⚠️ Skipping invalid link:', link);
        continue;
      }
      
      const conn: Connection = {
        source: link.source,
        target: link.target,
        weight: parseFloat(String(link.weight ?? 0)) || 0,
        count: parseInt(String(link.count ?? 0)) || 0,
        sentiment: parseFloat(String(link.sentiment ?? 0)) || 0,
        types: [],
        last_interaction: link.last_interaction || new Date().toISOString(),
      };
      
      const key = this.getConnectionKey(conn.source, conn.target);
      this.connections.set(key, conn);
      newConnections.push(conn);
    }

    onProgress({ connections: newConnections, progress: 90 });

    // Update stats
    if (result.meta) {
      this.stats.totalAgents = result.meta.totalAgents;
      console.log(`📊 Loaded ${result.meta.sampledAgents}/${result.meta.totalAgents} agents (sampled: ${result.meta.isSampled})`);
    }
    
    this.stats.loadedAgents = this.agents.size;
    this.stats.loadedConnections = this.connections.size;
  }

  /**
   * Load cluster view - for extremely large datasets (10M+ agents)
   */
  async loadClusterView(): Promise<GraphData> {
    interface ClusterResponse {
      nodes: Array<{
        id: string;
        name: string;
        main_submolt: string;
        karma: number;
        agent_count: number;
        avg_karma: number;
        _isCluster: boolean;
      }>;
      links: Array<{
        source: string;
        target: string;
        weight: number;
        count: number;
      }>;
    }

    const result = await fetch(`${CONFIG.API_BASE}/clusters`).then(r => r.json()) as ClusterResponse;
    
    const nodes: Agent[] = result.nodes.map(n => ({
      id: n.id,
      name: `${n.name} (${n.agent_count.toLocaleString()} agents)`,
      description: `Cluster with ${n.agent_count.toLocaleString()} agents, avg karma: ${n.avg_karma}`,
      main_submolt: n.main_submolt as any,
      karma: n.karma,
      post_count: 0,
      comment_count: 0,
      first_seen: '',
      last_active: '',
      is_bot: true,
      _isCluster: true,
      _clusterSize: n.agent_count,
    } as Agent & { _isCluster: boolean; _clusterSize: number }));

    const links: Connection[] = result.links.map(l => ({
      source: l.source,
      target: l.target,
      weight: parseFloat(String(l.weight)) || 0,
      count: parseInt(String(l.count)) || 0,
      sentiment: 0,
      types: [],
      last_interaction: '',
    }));

    return { nodes, links };
  }

  private async streamAgents(
    onProgress: StreamCallback,
    signal: AbortSignal
  ): Promise<void> {
    let page = 0;
    let hasMore = true;
    
    while (hasMore && !signal.aborted) {
      const result = await this.fetchWithRetry<{
        agents: Agent[];
        total: number;
      }>(`/agents?limit=${CONFIG.PAGE_SIZE}&offset=${page * CONFIG.PAGE_SIZE}`, signal);
      
      if (!result || !result.agents) {
        hasMore = false;
        continue;
      }

      // Process chunk
      // Parse numeric fields since DB/JSON may return strings
      const newAgents: Agent[] = [];
      for (const agent of result.agents) {
        if (!this.agents.has(agent.id)) {
          // Ensure numeric fields are actually numbers
          agent.karma = parseInt(String(agent.karma)) || 0;
          agent.post_count = parseInt(String(agent.post_count)) || 0;
          agent.comment_count = parseInt(String(agent.comment_count)) || 0;
          // Add computed birth time
          agent._birthTime = new Date(agent.first_seen).getTime();
          this.agents.set(agent.id, agent);
          newAgents.push(agent);
        }
      }

      this.stats.loadedAgents = this.agents.size;
      this.stats.totalAgents = result.total;
      
      // Calculate progress (agents = 60% of total)
      const agentProgress = Math.min((this.agents.size / result.total) * 60, 60);
      this.stats.streamProgress = agentProgress;
      
      onProgress({
        agents: newAgents,
        progress: agentProgress,
      });

      hasMore = result.agents.length === CONFIG.PAGE_SIZE;
      page++;

      // Yield to prevent blocking
      await this.sleep(CONFIG.STREAM_DELAY);
    }
  }

  private async streamConnections(
    onProgress: StreamCallback,
    signal: AbortSignal
  ): Promise<void> {
    // Fetch all connections (they're aggregated, so typically fewer than agents)
    // API returns source_id/target_id, we need to map to source/target
    interface ApiConnection {
      source_id: string;
      target_id: string;
      total_weight: number;
      interaction_count: number;
      avg_sentiment: number;
      interaction_types: string;
      last_interaction: string;
    }
    const result = await this.fetchWithRetry<ApiConnection[]>('/connections', signal);
    
    if (!result) return;

    const connections = Array.isArray(result) ? result : [];
    const newConnections: Connection[] = [];
    
    for (const apiConn of connections) {
      // Map API field names to our Connection type
      // Parse as numbers since DB/JSON may return strings
      const conn: Connection = {
        source: apiConn.source_id,
        target: apiConn.target_id,
        weight: parseFloat(String(apiConn.total_weight)) || 0,
        count: parseInt(String(apiConn.interaction_count)) || 0,
        sentiment: parseFloat(String(apiConn.avg_sentiment)) || 0,
        types: apiConn.interaction_types?.split(',') || [],
        last_interaction: apiConn.last_interaction,
      };
      
      const key = this.getConnectionKey(conn.source, conn.target);
      if (!this.connections.has(key)) {
        this.connections.set(key, conn);
        newConnections.push(conn);
      }
    }

    this.stats.loadedConnections = this.connections.size;
    this.stats.streamProgress = 100;
    
    onProgress({
      connections: newConnections,
      progress: 100,
    });
  }

  // ============ OPTIMIZED TIME MACHINE ============
  
  // Pre-sorted arrays for fast binary search (built lazily on first use)
  private sortedAgentsByTime: Agent[] = [];
  private sortedConnectionsByTime: Array<Connection & { _time: number }> = [];
  private timelapseCache = new Map<number, GraphData>();
  private readonly TIMELAPSE_CACHE_SIZE = 100;
  private timeArraysBuilt = false;
  private timeArraysBuildPromise: Promise<void> | null = null;
  
  /**
   * Build sorted time arrays LAZILY - only when Genesis mode is first used
   * This prevents blocking the UI on initial load
   */
  private buildTimeArrays(): void {
    if (this.timeArraysBuilt) return;
    
    console.log('⚡ Building optimized time arrays (lazy)...');
    const start = performance.now();
    
    // Sort agents by birth time
    this.sortedAgentsByTime = Array.from(this.agents.values())
      .map(a => ({
        ...a,
        _birthTime: a._birthTime || new Date(a.first_seen).getTime()
      }))
      .sort((a, b) => (a._birthTime || 0) - (b._birthTime || 0));
    
    // Sort connections by time
    this.sortedConnectionsByTime = Array.from(this.connections.values())
      .map(c => ({
        ...c,
        _time: new Date(c.last_interaction).getTime()
      }))
      .sort((a, b) => a._time - b._time);
    
    // Clear cache when data changes
    this.timelapseCache.clear();
    this.timeArraysBuilt = true;
    
    console.log(`⚡ Time arrays ready in ${(performance.now() - start).toFixed(0)}ms: ${this.sortedAgentsByTime.length} agents, ${this.sortedConnectionsByTime.length} connections`);
  }
  
  /**
   * Invalidate time arrays when data changes
   */
  private invalidateTimeArrays(): void {
    this.timeArraysBuilt = false;
    this.sortedAgentsByTime = [];
    this.sortedConnectionsByTime = [];
    this.timelapseCache.clear();
  }
  
  /**
   * Binary search to find index of last item <= timestamp
   * O(log n) instead of O(n)
   */
  private binarySearchTime<T>(arr: T[], timestamp: number, getTime: (item: T) => number): number {
    let left = 0;
    let right = arr.length - 1;
    let result = -1;
    
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      if (getTime(arr[mid]) <= timestamp) {
        result = mid;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }
    
    return result;
  }

  /**
   * Get the graph state at a specific timestamp
   * OPTIMIZED: Uses binary search on pre-sorted arrays + caching
   */
  getGraphAtTimestamp(timestamp: number): GraphData {
    // Cache by minute buckets to reduce computation
    const cacheKey = Math.floor(timestamp / 60000);
    const cached = this.timelapseCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    
    // Build arrays lazily on first use
    if (!this.timeArraysBuilt && this.agents.size > 0) {
      this.buildTimeArrays();
    }
    
    // Binary search for agents - O(log n)
    const agentCutoff = this.binarySearchTime(
      this.sortedAgentsByTime, 
      timestamp, 
      (a) => a._birthTime || 0
    );
    
    // Slice is O(k) where k is visible count, not O(n)
    const visibleNodes = agentCutoff >= 0 
      ? this.sortedAgentsByTime.slice(0, agentCutoff + 1)
      : [];
    
    // Build visible node set - O(k)
    const visibleNodeIds = new Set(visibleNodes.map(n => n.id));
    
    // Binary search for connections - O(log n)
    const connCutoff = this.binarySearchTime(
      this.sortedConnectionsByTime, 
      timestamp, 
      (c) => c._time
    );
    
    // Filter connections where both nodes visible
    // Only iterate up to cutoff, not full array
    const visibleLinks: Connection[] = [];
    for (let i = 0; i <= connCutoff && i < this.sortedConnectionsByTime.length; i++) {
      const c = this.sortedConnectionsByTime[i];
      if (visibleNodeIds.has(c.source) && visibleNodeIds.has(c.target)) {
        visibleLinks.push(c);
      }
    }

    const result = { nodes: visibleNodes, links: visibleLinks };
    
    // LRU cache - keep recent entries
    if (this.timelapseCache.size >= this.TIMELAPSE_CACHE_SIZE) {
      const firstKey = this.timelapseCache.keys().next().value;
      if (firstKey !== undefined) {
        this.timelapseCache.delete(firstKey);
      }
    }
    this.timelapseCache.set(cacheKey, result);
    
    return result;
  }

  /**
   * Get all time slices for the timeline
   * OPTIMIZED: Uses binary search counts instead of generating full graphs
   */
  getTimeSlices(bucketCount: number = 100): TimeSlice[] {
    if (this.agents.size === 0) return [];

    // Build arrays lazily on first use
    if (!this.timeArraysBuilt) {
      this.buildTimeArrays();
    }

    // Get time range from sorted array (O(1))
    const minTime = this.sortedAgentsByTime[0]?._birthTime || Date.now();
    const maxTime = this.sortedAgentsByTime[this.sortedAgentsByTime.length - 1]?._birthTime || Date.now();

    this.stats.oldestTimestamp = minTime;
    this.stats.newestTimestamp = maxTime;

    const bucketSize = (maxTime - minTime) / bucketCount;
    const slices: TimeSlice[] = [];

    // O(bucketCount * log n) instead of O(bucketCount * n)
    for (let i = 0; i <= bucketCount; i++) {
      const timestamp = minTime + (bucketSize * i);
      
      // Binary search for counts - O(log n) per bucket
      const agentCount = this.binarySearchTime(
        this.sortedAgentsByTime, 
        timestamp, 
        (a) => a._birthTime || 0
      ) + 1;
      
      const linkCount = this.binarySearchTime(
        this.sortedConnectionsByTime, 
        timestamp, 
        (c) => c._time
      ) + 1;
      
      slices.push({
        timestamp,
        nodeCount: Math.max(0, agentCount),
        linkCount: Math.max(0, linkCount),
      });
    }

    return slices;
  }

  /**
   * Get agents born within a time window (for supernova effects)
   */
  getNewbornAgents(startTime: number, endTime: number): Agent[] {
    const newborns: Agent[] = [];
    
    for (const agent of this.agents.values()) {
      const birthTime = agent._birthTime || new Date(agent.first_seen).getTime();
      if (birthTime > startTime && birthTime <= endTime) {
        newborns.push(agent);
      }
    }
    
    return newborns;
  }

  // ============ NEURAL PATHFINDER ============
  
  /**
   * Search agents by name or ID
   */
  searchAgents(query: string, limit: number = 20): Agent[] {
    if (!query || query.length < 2) return [];
    
    const lowerQuery = query.toLowerCase();
    const results: Array<{ agent: Agent; score: number }> = [];
    
    for (const agent of this.agents.values()) {
      let score = 0;
      
      // Exact ID match
      if (agent.id.toLowerCase() === lowerQuery) {
        score = 100;
      }
      // ID starts with query
      else if (agent.id.toLowerCase().startsWith(lowerQuery)) {
        score = 80;
      }
      // Name exact match
      else if (agent.name.toLowerCase() === lowerQuery) {
        score = 90;
      }
      // Name starts with query
      else if (agent.name.toLowerCase().startsWith(lowerQuery)) {
        score = 70;
      }
      // Name contains query
      else if (agent.name.toLowerCase().includes(lowerQuery)) {
        score = 50;
      }
      // Description contains query
      else if (agent.description?.toLowerCase().includes(lowerQuery)) {
        score = 30;
      }
      
      if (score > 0) {
        // Boost by karma
        score += Math.log10(Math.max(agent.karma, 1)) * 2;
        results.push({ agent, score });
      }
    }
    
    // Sort by score descending
    results.sort((a, b) => b.score - a.score);
    
    return results.slice(0, limit).map(r => r.agent);
  }

  /**
   * Get connections for an agent (1st and 2nd degree)
   * Returns agents with their interaction weight for ranking
   */
  getAgentConnections(agentId: string): {
    direct: Connection[];
    secondDegree: Connection[];
    influencers: Array<Agent & { _interactionWeight: number; _interactionCount: number }>;
    influenced: Array<Agent & { _interactionWeight: number; _interactionCount: number }>;
  } {
    const direct: Connection[] = [];
    const directIds = new Set<string>();
    const influencerMap = new Map<string, { agent: Agent; weight: number; count: number }>();
    const influencedMap = new Map<string, { agent: Agent; weight: number; count: number }>();
    
    // Find direct connections and aggregate by agent
    for (const conn of this.connections.values()) {
      if (conn.source === agentId) {
        direct.push({ ...conn, _degree: 1 });
        directIds.add(conn.target);
        const target = this.agents.get(conn.target);
        if (target) {
          const existing = influencedMap.get(conn.target);
          if (existing) {
            existing.weight += conn.weight || 1;
            existing.count += conn.count || 1;
          } else {
            influencedMap.set(conn.target, { 
              agent: target, 
              weight: conn.weight || 1, 
              count: conn.count || 1 
            });
          }
        }
      } else if (conn.target === agentId) {
        direct.push({ ...conn, _degree: 1 });
        directIds.add(conn.source);
        const source = this.agents.get(conn.source);
        if (source) {
          const existing = influencerMap.get(conn.source);
          if (existing) {
            existing.weight += conn.weight || 1;
            existing.count += conn.count || 1;
          } else {
            influencerMap.set(conn.source, { 
              agent: source, 
              weight: conn.weight || 1, 
              count: conn.count || 1 
            });
          }
        }
      }
    }

    // Find 2nd degree connections
    const secondDegree: Connection[] = [];
    for (const conn of this.connections.values()) {
      if (conn.source === agentId || conn.target === agentId) continue;
      if (directIds.has(conn.source) || directIds.has(conn.target)) {
        secondDegree.push({ ...conn, _degree: 2 });
      }
    }

    // Convert maps to arrays and sort by interaction weight (most interactions first)
    const influencers = Array.from(influencerMap.values())
      .sort((a, b) => b.weight - a.weight)
      .map(({ agent, weight, count }) => ({
        ...agent,
        _interactionWeight: weight,
        _interactionCount: count,
      }));

    const influenced = Array.from(influencedMap.values())
      .sort((a, b) => b.weight - a.weight)
      .map(({ agent, weight, count }) => ({
        ...agent,
        _interactionWeight: weight,
        _interactionCount: count,
      }));

    return { direct, secondDegree, influencers, influenced };
  }

  // ============ DATA ACCESS ============
  
  getAgent(id: string): Agent | undefined {
    return this.agents.get(id);
  }

  getAllAgents(): Agent[] {
    return Array.from(this.agents.values());
  }

  getAllConnections(): Connection[] {
    return Array.from(this.connections.values());
  }

  getFullGraph(): GraphData {
    return {
      nodes: this.getAllAgents(),
      links: this.getAllConnections(),
    };
  }

  getStats(): UniverseStats {
    return { ...this.stats, cacheSize: this.agents.size + this.connections.size };
  }
  
  /**
   * Clear all loaded data (for fresh reload)
   */
  clear(): void {
    this.agents.clear();
    this.connections.clear();
    this.timeIndex.clear();
    this.connectionIndex.clear();
    this.timelapseCache.clear();
    this.invalidateTimeArrays();
    this.stats.loadedAgents = 0;
    this.stats.loadedConnections = 0;
    this.stats.streamProgress = 0;
    console.log('🗑️ Universe data cleared');
  }

  // ============ CACHING ============
  
  private async loadFromCache(): Promise<boolean> {
    try {
      const version = await get(`${CONFIG.CACHE_PREFIX}version`);
      if (version !== CONFIG.CACHE_VERSION) {
        await this.clearCache();
        return false;
      }

      const agentsData = await get<Agent[]>(`${CONFIG.CACHE_PREFIX}agents`);
      const connectionsData = await get<Connection[]>(`${CONFIG.CACHE_PREFIX}connections`);
      
      if (agentsData) {
        for (const agent of agentsData) {
          agent._birthTime = new Date(agent.first_seen).getTime();
          this.agents.set(agent.id, agent);
        }
      }
      
      if (connectionsData) {
        for (const conn of connectionsData) {
          const key = this.getConnectionKey(conn.source, conn.target);
          this.connections.set(key, conn);
        }
      }

      this.stats.loadedAgents = this.agents.size;
      this.stats.loadedConnections = this.connections.size;
      
      // Rebuild indexes (time arrays built lazily on first Genesis use)
      this.buildTimeIndex();
      this.buildConnectionIndex();
      this.invalidateTimeArrays();

      return this.agents.size > 0;
    } catch (error) {
      console.error('Cache load failed:', error);
      return false;
    }
  }

  private async saveToCache(): Promise<void> {
    try {
      await set(`${CONFIG.CACHE_PREFIX}version`, CONFIG.CACHE_VERSION);
      await set(`${CONFIG.CACHE_PREFIX}agents`, this.getAllAgents());
      await set(`${CONFIG.CACHE_PREFIX}connections`, this.getAllConnections());
      console.log('💾 Saved to IndexedDB cache');
    } catch (error) {
      console.error('Cache save failed:', error);
    }
  }

  async clearCache(): Promise<void> {
    const allKeys = await keys();
    for (const key of allKeys) {
      if (String(key).startsWith(CONFIG.CACHE_PREFIX)) {
        await del(key);
      }
    }
    console.log('🗑️ Cache cleared');
  }

  // ============ NETWORK ============
  
  private async fetchWithRetry<T>(
    endpoint: string,
    signal: AbortSignal,
    retries = 0
  ): Promise<T | null> {
    try {
      const response = await fetch(`${CONFIG.API_BASE}${endpoint}`, { signal });
      
      if (response.status === 429) {
        // Rate limited - exponential backoff
        const backoff = Math.min(
          CONFIG.INITIAL_BACKOFF * Math.pow(2, retries),
          CONFIG.MAX_BACKOFF
        );
        console.warn(`⏳ Rate limited, waiting ${backoff}ms...`);
        await this.sleep(backoff);
        
        if (retries < CONFIG.MAX_RETRIES) {
          return this.fetchWithRetry<T>(endpoint, signal, retries + 1);
        }
        throw new Error('Max retries exceeded');
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      return response.json();
    } catch (error) {
      if ((error as Error).name === 'AbortError') throw error;
      
      if (retries < CONFIG.MAX_RETRIES) {
        const backoff = CONFIG.INITIAL_BACKOFF * Math.pow(2, retries);
        console.warn(`⚠️ Fetch failed, retry ${retries + 1}/${CONFIG.MAX_RETRIES} in ${backoff}ms`);
        await this.sleep(backoff);
        return this.fetchWithRetry<T>(endpoint, signal, retries + 1);
      }
      
      console.error(`❌ Fetch failed after ${CONFIG.MAX_RETRIES} retries:`, error);
      return null;
    }
  }

  async fetchStats(): Promise<void> {
    try {
      const stats = await fetch(`${CONFIG.API_BASE}/stats`).then(r => r.json());
      console.log('📊 Stats from API:', stats);
      this.stats.totalAgents = stats.agents || 0;
      this.stats.totalPosts = stats.posts || 0;
      this.stats.totalInteractions = stats.interactions || 0;
    } catch (error) {
      console.warn('Could not fetch stats:', error);
    }
  }

  // ============ INDEXING ============
  
  private buildTimeIndex(): void {
    this.timeIndex.clear();
    
    for (const agent of this.agents.values()) {
      const time = agent._birthTime || new Date(agent.first_seen).getTime();
      // Bucket by day
      const bucket = Math.floor(time / (24 * 60 * 60 * 1000));
      
      if (!this.timeIndex.has(bucket)) {
        this.timeIndex.set(bucket, new Set());
      }
      this.timeIndex.get(bucket)!.add(agent.id);
    }
  }

  private buildConnectionIndex(): void {
    this.connectionIndex.clear();
    
    for (const conn of this.connections.values()) {
      // Add bidirectional index
      if (!this.connectionIndex.has(conn.source)) {
        this.connectionIndex.set(conn.source, new Set());
      }
      this.connectionIndex.get(conn.source)!.add(conn.target);
      
      if (!this.connectionIndex.has(conn.target)) {
        this.connectionIndex.set(conn.target, new Set());
      }
      this.connectionIndex.get(conn.target)!.add(conn.source);
    }
  }

  // ============ UTILITIES ============
  
  private getConnectionKey(source: string, target: string): string {
    // Keep directional keys to preserve who influenced whom
    return `${source}::${target}`;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  stopStreaming(): void {
    if (this.abortController) {
      this.abortController.abort();
    }
  }
}

// ============ SINGLETON INSTANCE ============
export const universeLoader = new UniverseLoader();
